import { useState, useEffect, useRef } from 'react';
import { Modal, Button, Card, Typography, Flex, Tag } from 'antd';
import {
  WarningOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
  GiftOutlined,
  FireOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';

const { Text, Title } = Typography;

const GRACE_PERIOD_HOURS = 24;
const LS_OFFER_DISMISSED = "plan_renewal_offer_dismissed";
const LS_GRACE_DISMISSED = "plan_grace_dismissed";

function getDaysUntilRenew(renewDate) {
  if (!renewDate) return null;
  const renew = new Date(renewDate);
  if (isNaN(renew.getTime())) return null;
  return Math.ceil((renew.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function getHoursLeft(renewDate) {
  if (!renewDate) return 0;
  const renew = new Date(renewDate);
  if (isNaN(renew.getTime())) return 0;
  return Math.max(0, Math.floor((renew.getTime() - Date.now()) / (1000 * 60 * 60)));
}

function getGraceTimeLeft(renewDate) {
  if (!renewDate) return { hours: 0, minutes: 0, total: 0 };
  const renew = new Date(renewDate);
  if (isNaN(renew.getTime())) return { hours: 0, minutes: 0, total: 0 };
  const graceEnd = renew.getTime() + GRACE_PERIOD_HOURS * 60 * 60 * 1000;
  const diff = graceEnd - Date.now();
  if (diff <= 0) return { hours: 0, minutes: 0, total: 0 };
  return {
    hours: Math.floor(diff / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    total: diff,
  };
}

export default function PlanRenewalOffer({
  userPlan, userPlanLoading, onUpgrade,
}) {
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [showGraceModal, setShowGraceModal] = useState(false);
  const [showGraceBanner, setShowGraceBanner] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showOfferBanner, setShowOfferBanner] = useState(false);
  const [daysLeft, setDaysLeft] = useState(null);
  const [hoursLeft, setHoursLeft] = useState(0);
  const [graceTime, setGraceTime] = useState({ hours: 0, minutes: 0, total: 0 });
  const timerRef = useRef(null);

  const planData = userPlan?.data || userPlan;

  useEffect(() => {
    if (userPlanLoading || !planData?.plan_renew_date) return;
    if (planData?.plan_details?.id === 'free') return;

    const days = getDaysUntilRenew(planData.plan_renew_date);
    if (days === null) return;
    setDaysLeft(days);
    setHoursLeft(getHoursLeft(planData.plan_renew_date));

    if (days < 0) {
      const grace = getGraceTimeLeft(planData.plan_renew_date);
      if (grace.total > 0) {
        // Within grace period
        setGraceTime(grace);
        const dismissed = localStorage.getItem(LS_GRACE_DISMISSED);
        if (!dismissed) setShowGraceModal(true);
        setShowGraceBanner(true);
        // Start countdown timer
        timerRef.current = setInterval(() => {
          const updated = getGraceTimeLeft(planData.plan_renew_date);
          setGraceTime(updated);
          if (updated.total <= 0) {
            clearInterval(timerRef.current);
            setShowGraceBanner(false);
            setShowGraceModal(false);
            setShowExpiredModal(true);
          }
        }, 60000); // update every minute
      } else {
        // Grace period over → fully expired
        setShowExpiredModal(true);
      }
    } else if (days <= 2) {
      const dismissed = localStorage.getItem(LS_OFFER_DISMISSED);
      if (dismissed) {
        setShowOfferBanner(true);
      } else {
        setShowOfferModal(true);
        setShowOfferBanner(true);
      }
    }

    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [userPlan, userPlanLoading, planData?.plan_renew_date]);

  const handleDismissOffer = () => {
    localStorage.setItem(LS_OFFER_DISMISSED, Date.now().toString());
    setShowOfferModal(false);
  };

  const handleDismissGrace = () => {
    localStorage.setItem(LS_GRACE_DISMISSED, Date.now().toString());
    setShowGraceModal(false);
  };

  const urgencyText = daysLeft === 0
    ? `Only ${hoursLeft} hour${hoursLeft !== 1 ? 's' : ''} left`
    : daysLeft === 1
      ? 'Expires tomorrow'
      : `Only ${daysLeft} days left`;

  const graceCountdown = `${graceTime.hours}h ${graceTime.minutes}m`;

  // ── Fully Expired Modal (not closable) ──
  const expiredModal = (
    <Modal
      open={showExpiredModal}
      closable={false} footer={null} centered width={420}
      maskClosable={false} keyboard={false}
    >
      <div style={{ textAlign: 'center', padding: '24px 0 8px' }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%',
          background: '#fff1f0', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          <WarningOutlined style={{ fontSize: 36, color: '#ff4d4f' }} />
        </div>
        <Title level={3} style={{ margin: '0 0 8px', color: '#cf1322' }}>
          Your Plan Has Expired
        </Title>
        <Text style={{ fontSize: 15, color: '#595959', lineHeight: 1.6 }}>
          Your auto-renewal failed and your plan has expired.
          All automations are currently paused. Upgrade now to
          resume sending DMs and keep your audience engaged.
        </Text>
        <div style={{
          margin: '18px 0 0', padding: '12px 16px',
          background: '#f6ffed', borderRadius: 10,
          border: '1px solid #b7eb8f',
        }}>
          <Text style={{ fontSize: 13, color: '#389e0d' }}>
            💡 Tip: Switch to a yearly plan to skip monthly renewal
            headaches and get <span style={{ fontWeight: 700 }}>2 months free</span>.
          </Text>
        </div>
        <Button
          type="primary" danger size="large" block
          onClick={() => { setShowExpiredModal(false); onUpgrade(); }}
          style={{
            marginTop: 20, borderRadius: 10,
            height: 52, fontWeight: 700, fontSize: 16,
          }}
        >
          Upgrade Plan to Access
        </Button>
      </div>
    </Modal>
  );

  // ── Grace Period Modal (dismissable) ──
  const graceModal = (
    <Modal
      open={showGraceModal}
      closable={false} footer={null} centered width={440}
      maskClosable onCancel={handleDismissGrace}
    >
      <div style={{ padding: '16px 0 8px' }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <Tag
            color="red"
            style={{
              fontSize: 14, padding: '5px 16px',
              fontWeight: 700, borderRadius: 20,
            }}
          >
            <ExclamationCircleOutlined /> Auto-renewal failed
          </Tag>
        </div>

        <div style={{ textAlign: 'center' }}>
          <Title level={3} style={{ margin: '0 0 6px' }}>
            ⚠️ Automations Will Stop In
          </Title>
          {/* Countdown */}
          <div style={{
            display: 'inline-flex', gap: 8,
            margin: '12px 0 8px',
          }}>
            <div style={{
              background: '#ff4d4f', borderRadius: 10, padding: '10px 18px',
              color: '#fff', minWidth: 70, textAlign: 'center',
            }}>
              <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>
                {graceTime.hours}
              </div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>HOURS</div>
            </div>
            <div style={{
              background: '#ff4d4f', borderRadius: 10, padding: '10px 18px',
              color: '#fff', minWidth: 70, textAlign: 'center',
            }}>
              <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>
                {graceTime.minutes}
              </div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>MINS</div>
            </div>
          </div>
          <Text style={{ fontSize: 14, color: '#595959', display: 'block', marginTop: 6 }}>
            Your payment didn't go through. Renew within the
            grace period or all DM automations will be paused.
          </Text>
        </div>

        <div style={{
          margin: '18px 0', padding: '12px 16px',
          background: '#f6ffed', borderRadius: 10,
          border: '1px solid #b7eb8f',
        }}>
          <Text style={{ fontSize: 13, color: '#389e0d' }}>
            💡 Switch to yearly  no more renewal failures and
            get <span style={{ fontWeight: 700 }}>2 months free</span>.
          </Text>
        </div>

        <Button
          type="primary" danger size="large" block
          icon={<ThunderboltOutlined />}
          onClick={() => { setShowGraceModal(false); onUpgrade(); }}
          style={{
            borderRadius: 10, height: 52,
            fontWeight: 700, fontSize: 16,
          }}
        >
          Renew Now
        </Button>
        <Button
          type="text" block
          onClick={handleDismissGrace}
          style={{ marginTop: 8, color: '#bfbfbf', fontSize: 13 }}
        >
          Dismiss
        </Button>
      </div>
    </Modal>
  );

  // ── Grace Period Banner ──
  const graceBanner = showGraceBanner && !showExpiredModal ? (
    <Card
      style={{
        marginBottom: 16, borderRadius: 12,
        background: 'linear-gradient(135deg, #fff1f0 0%, #fff2e8 100%)',
        border: '1px solid #ffa39e',
      }}
      styles={{ body: { padding: '14px 20px' } }}
    >
      <Flex align="center" justify="space-between" wrap="wrap" gap={12}>
        <Flex align="center" gap={12} style={{ flex: 1, minWidth: 220 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: '#fff1f0', display: 'flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <ExclamationCircleOutlined style={{ fontSize: 20, color: '#ff4d4f' }} />
          </div>
          <div>
            <Text strong style={{ fontSize: 14, color: '#cf1322' }}>
              ⚠️ Renewal failed  Automations stop in {graceCountdown}
            </Text>
            <Text style={{ fontSize: 12, display: 'block', color: '#8c8c8c' }}>
              Renew now or switch to yearly to get 2 months free.
            </Text>
          </div>
        </Flex>
        <Button
          type="primary" danger
          icon={<ThunderboltOutlined />}
          onClick={onUpgrade}
          style={{ borderRadius: 8, fontWeight: 600 }}
        >
          Renew Now
        </Button>
      </Flex>
    </Card>
  ) : null;

  // ── Special Offer Modal (expiring in ≤2 days) ──
  const offerModal = (
    <Modal
      open={showOfferModal}
      closable={false} footer={null} centered width={440}
      maskClosable onCancel={handleDismissOffer}
    >
      <div style={{ padding: '16px 0 8px' }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <Tag color="red" style={{
            fontSize: 13, padding: '4px 14px',
            fontWeight: 600, borderRadius: 20,
          }}>
            <FireOutlined /> {urgencyText}  Act Now
          </Tag>
        </div>
        <div style={{ textAlign: 'center' }}>
          <Title level={3} style={{ margin: '0 0 4px' }}>
            🎁 Special Offer: Go Yearly
          </Title>
          <Text style={{ fontSize: 15, color: '#595959' }}>
            Before your plan renews, lock in the best deal
          </Text>
        </div>
        <div style={{
          margin: '20px 0', padding: '20px',
          background: 'linear-gradient(135deg, #f6ffed 0%, #fcffe6 100%)',
          borderRadius: 12, border: '1px solid #b7eb8f',
          textAlign: 'center',
        }}>
          <Text style={{ fontSize: 14, color: '#389e0d' }}>YEARLY PLAN SAVES YOU</Text>
          <div style={{ margin: '6px 0' }}>
            <span style={{ fontSize: 36, fontWeight: 800, color: '#52c41a', lineHeight: 1 }}>
              2 Months Free
            </span>
          </div>
          <Text style={{ fontSize: 13, color: '#595959' }}>
            Pay for 10 months, get 12. That's over 16% off.
          </Text>
        </div>
        <div style={{
          padding: '14px 16px', background: '#fff2f0',
          borderRadius: 10, border: '1px solid #ffccc7', marginBottom: 20,
        }}>
          <Text style={{ fontSize: 13, color: '#cf1322' }}>
            <ClockCircleOutlined style={{ marginRight: 6 }} />
            Your plan auto-renews soon. If the renewal fails,
            all automations will stop and your audience won't
            receive DMs until you resubscribe.
          </Text>
        </div>
        <Button
          type="primary" size="large" block icon={<ThunderboltOutlined />}
          onClick={() => { setShowOfferModal(false); onUpgrade(); }}
          style={{
            borderRadius: 10, height: 52, fontWeight: 700, fontSize: 16,
            background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
            border: 'none',
          }}
        >
          Claim Yearly Offer Now
        </Button>
        <Button type="text" block onClick={handleDismissOffer}
          style={{ marginTop: 8, color: '#bfbfbf', fontSize: 13 }}
        >
          I'll risk it, remind me later
        </Button>
      </div>
    </Modal>
  );

  // ── Offer Banner (expiring in ≤2 days) ──
  const offerBanner = showOfferBanner && !showExpiredModal && !showGraceBanner ? (
    <Card
      style={{
        marginBottom: 16, borderRadius: 12,
        background: 'linear-gradient(135deg, #fff1f0 0%, #fff7e6 100%)',
        border: '1px solid #ffa39e',
      }}
      styles={{ body: { padding: '14px 20px' } }}
    >
      <Flex align="center" justify="space-between" wrap="wrap" gap={12}>
        <Flex align="center" gap={12} style={{ flex: 1, minWidth: 220 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: '#fff2e8', display: 'flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <FireOutlined style={{ fontSize: 20, color: '#fa541c' }} />
          </div>
          <div>
            <Text strong style={{ fontSize: 14, color: '#cf1322' }}>
              ⏰ {urgencyText}  Auto-renewal coming up
            </Text>
            <Text style={{ fontSize: 12, display: 'block', color: '#8c8c8c' }}>
              If renewal fails, automations stop. Switch to yearly and get 2 months free.
            </Text>
          </div>
        </Flex>
        <Button type="primary" icon={<GiftOutlined />} onClick={onUpgrade}
          style={{ borderRadius: 8, fontWeight: 600, background: '#fa541c', border: 'none' }}
        >
          Claim Offer
        </Button>
      </Flex>
    </Card>
  ) : null;

  return (
    <>
      {expiredModal}
      {graceModal}
      {graceBanner}
      {offerModal}
      {offerBanner}
    </>
  );
}
